import { useTheme } from '@liquidweb-dev/undercurrent/styles';
import SvgIcon, { SvgIconProps } from '@liquidweb-dev/undercurrent/SvgIcon';
import LoaderCircle from '@liquidweb-dev/undercurrent/icons/animated/LoaderCircle';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';

export type StatusDotVariant =
	| 'success'
	| 'warning'
	| 'danger'
	| 'neutral'
	| 'loading';

const useColors = (variant: StatusDotVariant) => {
	const theme = useTheme();
	const { vars } = theme;

	switch (variant) {
		case 'success':
			return {
				fill: vars.palette.uc.success.bg.hover,
				stroke: vars.palette.uc.success.border.strong,
			};

		case 'warning':
			return {
				fill: vars.palette.uc.warning.bg.hover,
				stroke: vars.palette.uc.warning.border.strong,
			};

		case 'danger':
			return {
				fill: vars.palette.uc.danger.bg.hover,
				stroke: vars.palette.uc.danger.border.strong,
			};

		case 'neutral':
		default:
			return {
				fill: vars.palette.uc.bg.medium,
				stroke: vars.palette.uc.border.medium,
			};
	}
};

export interface StatusDotProps extends SvgIconProps {
	variant: StatusDotVariant;
}

const StatusDot = ({
	variant,
	width = 16,
	height = 16,
	sx,
	...rest
}: StatusDotProps) => {
	const { fill, stroke } = useColors(variant);

	if (variant === 'loading') {
		return (
			<LoaderCircle
				{...rest}
				sx={combineSx(
					{
						width,
						height,
						scale: 1.2,
					},
					sx,
				)}
				strokeWidth={2}
			/>
		);
	}

	return (
		<SvgIcon
			width={width}
			height={height}
			viewBox="0 0 16 16"
			xmlns="http://www.w3.org/2000/svg"
			sx={combineSx(
				({ size }) => ({ fill: 'none', height: size.xS, width: size.xS }),
				sx,
			)}
			{...rest}
		>
			<g id="circle">
				<g id="Vector" opacity="0.8">
					<path
						d="M8.00586 13.8813C7.20117 13.8813 6.44336 13.7271 5.73242 13.4185C5.02539 13.1138 4.40039 12.6919 3.85742 12.1528C3.31836 11.6099 2.89453 10.9849 2.58594 10.2778C2.28125 9.56689 2.12891 8.80908 2.12891 8.00439C2.12891 7.19971 2.28125 6.44385 2.58594 5.73682C2.89453 5.02588 3.31836 4.40088 3.85742 3.86182C4.39648 3.31885 5.01953 2.89502 5.72656 2.59033C6.4375 2.28174 7.19531 2.12744 8 2.12744C8.80469 2.12744 9.5625 2.28174 10.2734 2.59033C10.9844 2.89502 11.6094 3.31885 12.1484 3.86182C12.6875 4.40088 13.1113 5.02588 13.4199 5.73682C13.7285 6.44385 13.8828 7.19971 13.8828 8.00439C13.8828 8.80908 13.7285 9.56689 13.4199 10.2778C13.1113 10.9849 12.6875 11.6099 12.1484 12.1528C11.6094 12.6919 10.9844 13.1138 10.2734 13.4185C9.56641 13.7271 8.81055 13.8813 8.00586 13.8813Z"
						fill={fill}
					/>
					<path
						d="M5.3296 1.6725C4.50704 2.02715 3.77809 2.5227 3.14904 3.15599C2.52023 3.78511 2.02616 4.51495 1.66863 5.33864L1.66862 5.33864L1.66758 5.34106C1.3066 6.17872 1.12891 7.06988 1.12891 8.00439C1.12891 8.93893 1.3066 9.83131 1.66679 10.6718L1.66677 10.6718L1.66943 10.6779C2.02712 11.4974 2.52077 12.2258 3.14777 12.8574L3.14775 12.8574L3.15287 12.8625C3.78501 13.4901 4.51447 13.9823 5.33532 14.3362C6.17643 14.7012 7.06994 14.8813 8.00586 14.8813C8.94105 14.8813 9.83271 14.7014 10.6705 14.3363C11.4953 13.9823 12.226 13.4895 12.8555 12.8599L12.8581 12.8574C13.4851 12.2258 13.9787 11.4974 14.3364 10.6779L14.3372 10.676C14.7025 9.83458 14.8828 8.94071 14.8828 8.00439C14.8828 7.06844 14.7026 6.17609 14.3368 5.33772C13.9793 4.51441 13.4854 3.78488 12.8568 3.15599C12.2271 2.52203 11.4957 2.02653 10.6695 1.67212C9.82871 1.30749 8.93554 1.12744 8 1.12744C7.06413 1.12744 6.17067 1.30762 5.3296 1.6725Z"
						stroke={stroke}
						strokeOpacity="0.5"
						strokeWidth="2"
					/>
				</g>
			</g>
		</SvgIcon>
	);
};

export default StatusDot;
